// 5th form
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Upload,
  Trash2,
  FileText,
  Image as ImageIcon,
  Loader2,
  ShieldAlert,
  Image,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { registerSupplierAction } from '../../../reduxstore/actions/authActions';
import { LockClosedIcon } from '@radix-ui/react-icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';
import {
  fetchExistingDocuments,
  FileUploadBox,
} from '@/js/components/FileUploadBox';
import UploadDocumentModal from '@/js/components/modals/UploadDocumentModal';
import FileUpload from '@/js/components/FileUpload';

const DOCUMENT_TITLES = {
  iso_13485: 'ISO 13485',
  iso_ts_9000: 'ISO/TS 16949',
  iso_9000: 'ISO 9000',
  iso_14644: 'ISO 14644',
  iso_14001: 'ISO 14001',
  iso_14937: 'ISO 14937',
  iso_15378: 'ISO 15378',
  iso_17665: 'ISO 17665',
  fda_approvals: 'FDA Approvals',
  other_certificate: 'Other Certificate',
};

// max file size is 1MB
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes

const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'image/webp',
  'image/jpeg',
  'image/png',
  'image/jpg',
];

const UploadCertificates = ({ setSectionId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiClient = createApiClient(navigate, dispatch);

  const reduxData = useSelector(
    (state) => state?.authReducer?.registrationData,
  );
  const token = useSelector((state) => state.authReducer.token);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Initialize states from Redux data
  const [documentStates, setDocumentStates] = useState({
    iso_13485: reduxData?.iso_13485 || null,
    iso_ts_9000: reduxData?.qs_9000 || null,
    iso_9000: reduxData?.iso_9000 || null,
    iso_14644: reduxData?.iso_14644 || null,
    iso_14001: reduxData?.iso_14001 || null,
    iso_14937: reduxData?.iso_14937 || null,
    iso_15378: reduxData?.iso_15378 || null,
    iso_17665: reduxData?.iso_17665 || null,
    fda_approvals: reduxData?.fda_approvals || null,
    other_certificate: reduxData?.other_certificate || null,
  });

  // Upload progress for each document
  const [uploadProgress, setUploadProgress] = useState({
    iso_13485: 0,
    iso_ts_9000: 0,
    iso_9000: 0,
    iso_14644: 0,
    iso_14001: 0,
    iso_14937: 0,
    iso_15378: 0,
    iso_17665: 0,
    fda_approvals: 0,
    other_certificate: 0,
  });

  // Fetch existing documents on component mount
  useEffect(() => {
    fetchExistingDocuments(apiClient, setExistingDocuments, setError);
  }, [token]);

  // 5. Function to submit the form
  const onSubmit = (data) => {
    dispatch(
      registerSupplierAction({
        ...reduxData,
        ...documentStates,
      }),
    );
    console.log('Certificate Data: ', data);
    setSectionId(5);
  };

  // ----------------------------------------------------------- //
  return (
    <div className="w-full bg-white px-28 rounded-md font-roboto text-gray-700">
      <h2 className="text-3xl font-semibold text-black text-center mb-4">
        Upload Certificates
      </h2>
      <p className="text-center text-[#637381] mb-8 text-base">
        Please add specific information to impress our buyers with your capacity
        and capabilities
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        className="space-y-6"
      >
        {/* Grid to arrange the 6 file upload buttons */}
        {/* Grid to arrange the 10 file upload buttons in 3 columns per row */}
        <div className="grid grid-cols-2 gap-4">
          {[
            { name: 'iso_13485', label: 'ISO 13485' },
            { name: 'iso_ts_9000', label: 'ISO/TS 16949' },
            { name: 'iso_9000', label: 'ISO 9000' },
            { name: 'iso_14644', label: 'ISO 14644' },
            { name: 'iso_14001', label: 'ISO 14001' },
            { name: 'iso_14937', label: 'ISO 14937' },
            { name: 'iso_15378', label: 'ISO 15378' },
            { name: 'iso_17665', label: 'ISO 17665' },
            { name: 'fda_approvals', label: 'FDA Approvals' },
            { name: 'other_certificate', label: 'Other Certificate' },
          ].map(({ name, label }) => (
            <div
              className="p-1 px-2 border border-gray-300 space-y-1 rounded-lg"
              key={name}
            >
              <Label className="text-sm font-semibold">{label}</Label>
              <FileUpload
                DOCUMENT_TITLES={DOCUMENT_TITLES}
                MAX_FILE_SIZE={MAX_FILE_SIZE}
                ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                existingDocuments={existingDocuments}
                setExistingDocuments={setExistingDocuments}
                documentStates={documentStates}
                setDocumentStates={setDocumentStates}
                uploadProgress={uploadProgress}
                setUploadProgress={setUploadProgress}
                isUploading={isUploading}
                setIsUploading={setIsUploading}
                setError={setError}
                isUploadingCertificate={true}
                key={name}
                id={name}
                label={label}
                documentType={name}
                existingDocument={existingDocuments.find(
                  (doc) => doc.document_name === name,
                )}
                classNames="w-full h-24"
              />
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-8 text-lg">
          <Button
            type="button"
            variant="outline"
            onClick={() => setSectionId(3)}
            className="px-4 border border-gray-300 py-2 h-12 w-36 hover:bg-slate-200"
          >
            Back
          </Button>
          <Button
            onClick={() => setSectionId(5)}
            className="px-4 py-2 h-12 w-36 bg-darkPurple hover:bg-darkerPurple ease-in duration-300"
            disabled={processing || isUploading}
          >
            {processing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span className="font-roboto font-semibold text-base">
                  Saving...
                </span>
              </>
            ) : isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span className="font-roboto font-semibold text-base">
                  Uploading...
                </span>
              </>
            ) : (
              <span className="font-roboto font-semibold text-base">Next</span>
            )}
          </Button>
        </div>

        <div className="flex flex-row items-center justify-center space-x-2 ">
          {error ? (
            <>
              <ShieldAlert className="h-3 w-3" />
              <span className="text-xs text-red-600">{error}</span>
            </>
          ) : (
            <>
              <LockClosedIcon className="h-3 w-3" />
              <span className="text-xs text-[#2CD673]">
                We use industry-standard encryption to protect your data
              </span>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default UploadCertificates;
